import {
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  sendPasswordResetEmail,
  type User,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from './firebase';

export type Role = 'admin' | 'user';

export interface Session {
  user: User;
  role: Role | null;  // null = signed in but not allowlisted
}

export function signIn(): Promise<User> {
  return signInWithPopup(auth, googleProvider).then((cred) => cred.user);
}

export function signInEmail(email: string, password: string): Promise<User> {
  return signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password).then((cred) => cred.user);
}

// QA harness entry (card um8gehdw): consumes a custom token minted server-side
// by qa/mint-demo-session.mjs with Admin-SDK credentials. This is not a lower
// trust bar than the Google-popup or email/password paths above — a custom
// token is only ever produced by a caller who already holds privileged
// Firebase Admin credentials (the same bar `evals/run-evals.mjs` mints
// against), never by anything reachable from this page. main.ts is the only
// caller, gated behind the `qaCustomToken` URL param it strips immediately
// after use.
export function signInWithQaToken(token: string): Promise<User> {
  return signInWithCustomToken(auth, token).then((cred) => cred.user);
}

// NOTE ([PHASE 2] XDKuaRjc): no public self-signup. Accounts are provisioned
// by an admin via the invite flow (proxy /admin/users/invite); the allowlist
// stays the gate either way.
export function resetPassword(email: string): Promise<void> {
  return sendPasswordResetEmail(auth, email.trim().toLowerCase());
}

export function signOutNow(): Promise<void> {
  return signOut(auth);
}

export async function resolveRole(user: User): Promise<Role | null> {
  if (!user.email) return null;
  const snap = await getDoc(doc(db, 'studioConsoleAllowlist', user.email.toLowerCase()));
  if (!snap.exists()) return null;
  const data = snap.data() as { role?: Role };
  return data.role === 'admin' ? 'admin' : data.role === 'user' ? 'user' : null;
}

export function onSession(cb: (session: Session | null) => void): () => void {
  return onAuthStateChanged(auth, async (user) => {
    if (!user) { cb(null); return; }
    try {
      const role = await resolveRole(user);
      cb({ user, role });
    } catch (e) {
      console.error('[auth] resolveRole failed', e);
      cb({ user, role: null });
    }
  });
}
